import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@produzionejb/ui';
import { useFinishedBoxes, useScanBox } from '../lib/spedizioni-api';

/**
 * M4 UI: piccolo controllo per allocare FinishedBox a una Spedizione (MSS/MSF)
 * in DRAFT.
 * - Input QR (scansiona o digita) -> POST /scan (singola scatola)
 * - Search SKU/lotto -> mostra lista scatole PRONTA -> click per allocare
 * Tutte le mutazioni invalidano cache, il form parent ricarica le righe.
 */
export function BoxAllocator({
  docId,
  disabled,
}: {
  docId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const scanMut = useScanBox(docId);
  const boxesQ = useFinishedBoxes({
    status: 'PRONTA',
    pageSize: 50,
    ...(search.trim() && { search: search.trim() }),
  });

  async function handleScan(e?: FormEvent) {
    e?.preventDefault();
    if (!qrCode.trim()) return;
    try {
      await scanMut.mutateAsync(qrCode.trim().toUpperCase());
      setQrCode('');
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleAllocate(qr: string) {
    try {
      await scanMut.mutateAsync(qr);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  if (disabled) return null;

  return (
    <div className="rounded-md border border-brand-200 bg-brand-50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brand-700">
          📦 {t('box.allocator.title')}
        </h3>
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          className="text-xs text-brand-700 hover:underline"
        >
          {showPicker ? t('box.allocator.hidePicker') : t('box.allocator.showPicker')}
        </button>
      </div>

      <form onSubmit={(e) => void handleScan(e)} className="flex gap-2">
        <Input
          autoFocus
          placeholder={t('box.allocator.qrPlaceholder')}
          value={qrCode}
          onChange={(e) => setQrCode(e.target.value)}
          className="font-mono uppercase"
        />
        <Button type="submit" size="sm" disabled={!qrCode.trim() || scanMut.isPending}>
          {scanMut.isPending ? '...' : t('box.allocator.scanButton')}
        </Button>
      </form>

      {showPicker && (
        <div className="space-y-2 border-t border-brand-200 pt-2">
          <Input
            placeholder={t('box.allocator.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="max-h-64 overflow-y-auto rounded border border-brand-200 bg-white">
            {boxesQ.isLoading && (
              <p className="p-2 text-xs text-slate-500">{t('common.loading')}</p>
            )}
            {boxesQ.data && boxesQ.data.items.length === 0 && (
              <p className="p-2 text-xs text-slate-500">{t('box.allocator.empty')}</p>
            )}
            {boxesQ.data?.items.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => void handleAllocate(b.qrCode)}
                className="block w-full border-b border-slate-100 px-2 py-1 text-left text-xs hover:bg-brand-50"
              >
                <span className="font-mono text-brand-700">{b.qrCode}</span>
                <span className="mx-2 text-slate-400">|</span>
                {b.productDriveLegendId !== null && (
                  <span className="mr-1 rounded bg-slate-100 px-1 font-mono text-[10px]">
                    {b.productDriveLegendId}
                  </span>
                )}
                <span className="font-medium">{b.productName}</span>
                <span className="ml-1 text-slate-500">
                  {b.productSkuGrammatura}g × {b.qtyPieces}pz
                </span>
                <span className="ml-2 text-slate-400">lotto {b.lotCode}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
